"use client"

import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Button, Drawer, IconButton, Typography } from "@mui/material"
import Image from "next/image"
import Filters from "./Filters"
import { SearchFilters, SearchFiltersActions } from "./Filters/useSearchFilters"

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 100%;
    height: 100%;
    background-color: ${sharedColors.white};
    display: flex;
    flex-direction: column;
  }
`

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #aaaaaa;
`

/*
 * overflow-x is pinned rather than left to default (END-286). A box that sets only
 * overflow-y to auto gets overflow-x: auto as well, because CSS promotes a visible
 * axis to auto when the other axis is not visible. That turned every stray pixel a
 * filter section poked past the column into a sideways swipe over the whole panel.
 */
const StyledContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
`

const StyledFooter = styled.div`
  padding: 16px 16px 24px;
  background-color: ${sharedColors.white};
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.16);
`

const StyledApplyButton = styled(Button)`
  width: 100%;
  padding: 12px 24px;
  background-color: ${sharedColors.darkGreen};
  color: ${sharedColors.white};
  border-radius: 8px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;

  &:hover {
    background-color: ${sharedColors.darkGreen};
    opacity: 0.9;
  }
`

const StyledFiltersContainer = styled.div`
  .MuiTypography-h3 {
    display: none; // Hide the title in mobile view since it's in the header
  }
`

interface MobileFiltersDrawerProps {
  open: boolean
  onClose: () => void
  filters: SearchFilters
  actions: SearchFiltersActions
  carFeatures: Array<{ id: string; label: string }>
  carTypes: Array<{ id: string; label: string; icon: string }>
  manufacturers: Array<{
    value: string
    label: string
    models: Array<{ value: string; label: string }>
  }>
  resultsCount: number
  minPrice: number
  maxPrice: number
}

const MobileFiltersDrawer = ({
  open,
  onClose,
  filters,
  actions,
  carFeatures,
  carTypes,
  manufacturers,
  resultsCount,
  minPrice,
  maxPrice,
}: MobileFiltersDrawerProps) => {
  // Calculate the actual number of selected filters
  const getSelectedFiltersCount = () => {
    let count = 0

    // Search query
    if (filters.searchQuery) count++

    // Selected features
    count += filters.selectedFeatures.length

    // Selected car types
    count += filters.selectedCarTypes.length

    // Price range (if different from default)
    if (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 0) count++

    // Year range - only count for used cars
    if (filters.dealType === "used" && (filters.yearFrom || filters.yearTo))
      count++

    // KM range - only count for used cars
    if (filters.dealType === "used" && (filters.kmFrom || filters.kmTo)) count++

    // Manufacturer
    if (filters.selectedManufacturer) count++

    // Model
    if (filters.selectedModel) count++

    return count
  }

  const selectedFiltersCount = getSelectedFiltersCount()

  const handleApply = () => {
    onClose()
  }

  return (
    <StyledDrawer anchor="bottom" open={open} onClose={onClose}>
      <StyledHeader>
        <Typography
          variant="h6"
          fontWeight={400}
          fontSize={24}
          lineHeight={1.4}
        >
          ({selectedFiltersCount}) סינון רכבים
        </Typography>
        <IconButton onClick={onClose} aria-label="סגור סינונים">
          <Image
            src="/icons/close-icon.svg"
            alt=""
            width={24}
            height={24}
            style={{
              filter: `brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)`, // Convert to black
            }}
          />
        </IconButton>
      </StyledHeader>

      <StyledContent>
        <StyledFiltersContainer>
          <Filters
            filters={filters}
            actions={actions}
            carFeatures={carFeatures}
            carTypes={carTypes}
            manufacturers={manufacturers}
            isMobile={true}
            minPrice={minPrice}
            maxPrice={maxPrice}
          />
        </StyledFiltersContainer>
      </StyledContent>

      <StyledFooter>
        <Button variant="primary" fullWidth size="large" onClick={handleApply}>
          {resultsCount} תוצאות
        </Button>
      </StyledFooter>
    </StyledDrawer>
  )
}

export default MobileFiltersDrawer
